import React, { useState } from 'react';

const LearningJourneyPage = ({ language = 'traditional', onBack, onOpen }) => {
  const [completedFeatures, setCompletedFeatures] = useState(new Set(['vocabulary'])); // Demo: vocabulary is completed

  const learningSequence = [
    // Foundation Phase
    {
      phase: 'foundation',
      title: { 
        traditional: '基礎階段', 
        simplified: '基础阶段', 
        english: 'Foundation Phase' 
      },
      features: [
        {
          id: 'vocabulary',
          icon: '📚',
          title: { traditional: '詞彙學習', simplified: '词汇学习', english: 'Vocabulary' },
          description: { traditional: '建立基礎詞彙量', simplified: '建立基础词汇量', english: 'Build your vocabulary foundation' },
          difficulty: 'beginner',
          estimatedTime: '15-20 min',
          isUnlocked: true
        },
        {
          id: 'grammarTopics',
          icon: '📝',
          title: { traditional: '語法主題', simplified: '语法主题', english: 'Grammar Topics' },
          description: { traditional: '掌握核心語法規則', simplified: '掌握核心语法规则', english: 'Master core grammar rules' },
          difficulty: 'beginner',
          estimatedTime: '20-25 min',
          isUnlocked: true
        },
        {
          id: 'lessons',
          icon: '📖',
          title: { traditional: '課程學習', simplified: '课程学习', english: 'Lessons' },
          description: { traditional: '結構化學習內容', simplified: '结构化学习内容', english: 'Structured learning content' },
          difficulty: 'beginner',
          estimatedTime: '25-30 min',
          isUnlocked: true
        }
      ]
    },
    // Practice Phase
    {
      phase: 'practice',
      title: { 
        traditional: '練習階段', 
        simplified: '练习阶段', 
        english: 'Practice Phase' 
      },
      features: [
        {
          id: 'unitStudying',
          icon: '🎯',
          title: { traditional: '單元學習', simplified: '单元学习', english: 'Unit Studying' },
          description: { traditional: '深入學習特定主題', simplified: '深入学习特定主题', english: 'Deep dive into specific topics' },
          difficulty: 'intermediate',
          estimatedTime: '30-40 min',
          isUnlocked: true
        },
        {
          id: 'dailyChallenges',
          icon: '🏆',
          title: { traditional: '每日挑戰', simplified: '每日挑战', english: 'Daily Challenges' },
          description: { traditional: '每日練習保持進步', simplified: '每日练习保持进步', english: 'Daily practice to maintain progress' },
          difficulty: 'beginner',
          estimatedTime: '10-15 min',
          isUnlocked: true
        },
        {
          id: 'rolePlay',
          icon: '🎭',
          title: { traditional: '角色扮演', simplified: '角色扮演', english: 'Role Play' },
          description: { traditional: '情境對話練習', simplified: '情境对话练习', english: 'Practice conversations in scenarios' },
          difficulty: 'intermediate',
          estimatedTime: '20-25 min',
          isUnlocked: true
        }
      ]
    },
    // Advanced Phase
    {
      phase: 'advanced',
      title: { 
        traditional: '進階階段', 
        simplified: '进阶阶段', 
        english: 'Advanced Phase' 
      },
      features: [
        {
          id: 'toneLearning',
          icon: '🎶',
          title: { traditional: '聲調學習', simplified: '声调学习', english: 'Tone Learning' },
          description: { traditional: '掌握語音和聲調', simplified: '掌握语音和声调', english: 'Master pronunciation and tones' },
          difficulty: 'intermediate',
          estimatedTime: '25-30 min',
          isUnlocked: true
        },
        {
          id: 'arPractice',
          icon: '🌐',
          title: { traditional: 'AR實境練習', simplified: 'AR实境练习', english: 'AR Practice' },
          description: { traditional: '沉浸式實境應用', simplified: '沉浸式实境应用', english: 'Immersive real-world application' },
          difficulty: 'advanced',
          estimatedTime: '30-45 min',
          isUnlocked: true
        },
        {
          id: 'learningCycle',
          icon: '🔄',
          title: { traditional: '學習循環', simplified: '学习循环', english: 'Learning Cycle' },
          description: { traditional: '複習和鞏固知識', simplified: '复习和巩固知识', english: 'Review and reinforce knowledge' },
          difficulty: 'intermediate',
          estimatedTime: '20-25 min',
          isUnlocked: true
        }
      ]
    },
    // Support Phase
    {
      phase: 'support',
      title: { 
        traditional: '支援階段', 
        simplified: '支援阶段', 
        english: 'Support Phase' 
      },
      features: [
        {
          id: 'bilingualTutor',
          icon: '🧑‍🏫',
          title: { traditional: '雙語導師', simplified: '双语导师', english: 'Bilingual Tutor' },
          description: { traditional: '個人化指導和反饋', simplified: '个性化指导和反馈', english: 'Personalized guidance and feedback' },
          difficulty: 'all',
          estimatedTime: '15-30 min',
          isUnlocked: true
        },
        {
          id: 'community',
          icon: '🤝',
          title: { traditional: '學習社群', simplified: '学习社群', english: 'Community' },
          description: { traditional: '與其他學習者交流', simplified: '与其他学习者交流', english: 'Connect with other learners' },
          difficulty: 'all',
          estimatedTime: '10-20 min',
          isUnlocked: true
        },
        {
          id: 'communityLearning',
          icon: '🧑‍🤝‍🧑',
          title: { traditional: '社群學習', simplified: '社群学习', english: 'Community Learning' },
          description: { traditional: '協作學習活動', simplified: '协作学习活动', english: 'Collaborative learning activities' },
          difficulty: 'all',
          estimatedTime: '20-30 min',
          isUnlocked: true
        }
      ]
    },
    // Progress Phase
    {
      phase: 'progress',
      title: { 
        traditional: '進度追蹤', 
        simplified: '进度追踪', 
        english: 'Progress Tracking' 
      },
      features: [
        {
          id: 'learningPath',
          icon: '🗺️',
          title: { traditional: '學習路徑', simplified: '学习路径', english: 'Learning Path' },
          description: { traditional: '個人化學習規劃', simplified: '个性化学习规划', english: 'Personalized learning roadmap' },
          difficulty: 'all',
          estimatedTime: '5-10 min',
          isUnlocked: true
        },
        {
          id: 'userProgress',
          icon: '📈',
          title: { traditional: '學習進度', simplified: '学习进度', english: 'User Progress' },
          description: { traditional: '追蹤學習成就', simplified: '追踪学习成就', english: 'Track your learning achievements' },
          difficulty: 'all',
          estimatedTime: '5-10 min',
          isUnlocked: true
        }
      ]
    }
  ];

  const getText = (textObj) => {
    return textObj[language] || textObj.traditional;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'intermediate': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'advanced': return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'all': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const handleFeatureClick = (feature) => {
    if (feature.isUnlocked) {
      onOpen(feature.id);
    }
  };

  const getPhaseIcon = (phase) => {
    switch (phase) {
      case 'foundation': return '🌱';
      case 'practice': return '💪';
      case 'advanced': return '🚀';
      case 'support': return '🤝';
      case 'progress': return '📊';
      default: return '⭐';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white">
      {/* Header */}
      <div className="px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="text-white">
            ←
          </button>
          <h1 className="text-xl font-bold">
            {language === 'traditional' ? '學習旅程' :
             language === 'simplified' ? '学习旅程' :
             'Learning Journey'}
          </h1>
          <div className="w-8"></div>
        </div>

        {/* Description */}
        <div className="mb-8 text-center">
          <p className="text-gray-300 text-sm max-w-2xl mx-auto">
            {language === 'traditional' ? '跟隨引導路徑掌握您的語言技能。點擊每個功能開始或繼續學習。' :
             language === 'simplified' ? '跟随引导路径掌握您的语言技能。点击每个功能开始或继续学习。' :
             'Follow a guided path to master your language skills. Click on each feature to start or continue learning.'}
          </p>
        </div>
      </div>

      {/* Learning Sequence */}
      <div className="px-6 pb-20">
        <div className="space-y-8">
          {learningSequence.map((phase, phaseIndex) => (
            <div key={phase.phase} className="relative">
              {/* Phase Header */}
              <div className="flex items-center mb-6">
                <div className="text-3xl mr-4">{getPhaseIcon(phase.phase)}</div>
                <div>
                  <h2 className="text-lg font-bold text-white">
                    {getText(phase.title)}
                  </h2>
                  <div className="w-16 h-1 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full mt-1"></div>
                </div>
              </div>

              {/* Features Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {phase.features.map((feature, featureIndex) => {
                  const isCompleted = completedFeatures.has(feature.id);
                  const isNext = phaseIndex === 0 && featureIndex === 0; // First feature is always next
                  
                  return (
                    <div
                      key={feature.id}
                      onClick={() => handleFeatureClick(feature)}
                      className={`relative bg-gray-800/30 border rounded-xl p-4 cursor-pointer transition-all duration-200 ease-out hover:scale-105 active:scale-95 ${
                        feature.isUnlocked 
                          ? 'hover:bg-gray-700/50 hover:border-gray-500' 
                          : 'opacity-50 cursor-not-allowed'
                      } ${isCompleted ? 'border-green-500/50' : 'border-gray-600'}`}
                    >
                      {/* Status Indicator */}
                      <div className="absolute top-3 right-3">
                        {isCompleted ? (
                          <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        ) : isNext ? (
                          <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center animate-pulse">
                            <span className="text-white text-xs">→</span>
                          </div>
                        ) : (
                          <div className="w-6 h-6 border-2 border-gray-400 rounded-full"></div>
                        )}
                      </div>

                      {/* Feature Icon */}
                      <div className="text-3xl mb-3">{feature.icon}</div>

                      {/* Feature Title */}
                      <h3 className="font-semibold text-white mb-2">
                        {getText(feature.title)}
                      </h3>

                      {/* Feature Description */}
                      <p className="text-gray-400 text-sm mb-3">
                        {getText(feature.description)}
                      </p>

                      {/* Difficulty and Time */}
                      <div className="flex items-center justify-between mb-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${getDifficultyColor(feature.difficulty)}`}>
                          {feature.difficulty.toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-400">
                          {feature.estimatedTime}
                        </span>
                      </div>

                      {/* Progress Bar (if completed) */}
                      {isCompleted && (
                        <div className="w-full bg-gray-700 rounded-full h-1">
                          <div className="bg-green-500 h-1 rounded-full" style={{ width: '100%' }}></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Phase Connector */}
              {phaseIndex < learningSequence.length - 1 && (
                <div className="flex justify-center mt-8">
                  <div className="w-1 h-8 bg-gradient-to-b from-blue-400 to-purple-400 rounded-full"></div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LearningJourneyPage;
